import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, computed, watch } from "vue";
  import SetExpirationDateHoldRequestModal from "@/components/ui/modals/SetExpirationDateHoldRequestModal.vue";
  import { useToast } from "vue-toastification";
  import router from "@/router";
  import { LotInterface } from "@/types/LotInterface";
  import store from "@/store";
  import ConfirmInitialHoldModal from "@/components/ui/modals/ConfirmInitialHoldModal.vue";
  import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import {
    InitialHoldLeadsDetails,
    InitialHoldInterface,
  } from "@/types/InitialHoldInterface";
  import { formatDate } from "@/helpers/date";
  import Breadcrumbs from "@/components/ui/Breadcrumbs.vue";
  import InitialHoldStatus from "@/enums/initialHoldStatus";
  import { LotStatus } from "@/enums/LotStatus";
  import { isInitialHoldDisabled } from "@/helpers/initialHold/isInitialHoldDisabled";
  import { getInitialHoldStatusText } from "@/helpers/initialHold/getInitialHoldStatusText";
  import GiveReasonModal from "@/components/ui/modals/GiveReasonModal.vue";
  import InitialHoldLotDetailsCard from "@/components/initial-hold/InitialHoldLotDetailsCard.vue";
  import InitialHoldDetailsCard from "@/components/initial-hold/InitialHoldDetailsCard.vue";
  import { capitalizeString } from "@/helpers/transformation/string";
  import {
    ReasonsForDecline,
    ReasonsForCancel,
    ReasonsForExpire,
  } from "@/enums/ReasonsForTerminationHold";

  import { getInitialHoldStatusClass } from "@/helpers/status";
  import { isLotOwner } from "@/helpers/lot/isLotOwner";
  import { Business } from "@/types/BusinessInterface";
  import getUserData from "@/helpers/listing/getUserData";
  import { AxiosResponse } from "axios";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'InitialHoldDetails',
  setup(__props, { expose: __expose }) {
  __expose();

  const loading = ref(false);
  const toast = useToast();
  const stage = ref();
  const disableCancelButton = ref(false);
  const initialHold = ref<InitialHoldInterface | null>(null);
  const purchaserDetails = ref<InitialHoldLeadsDetails | null>(null);
  const lot = ref<LotInterface | null>(null);
  const userBusiness = ref<Business | null>(null);
  const isOpenApproveModal = ref(false);
  const disabledApproveButton = ref(false);
  const disabledDeclineButton = ref(false);
  const isConfirmCancelModalOpen = ref(false);
  const isConfirmApproveModalOpen = ref(false);
  const isDeclineModalOpen = ref(false);
  const isExtendModalOpen = ref(false);
  const isModalSubmitting = ref(false);
  const isReasonForCancelModalOpen = ref(false);
  const isReasonForExpiredModalOpen = ref(false);
  const pendingInitialHoldsOnLotCount = ref(0);
  const estateId = computed(() => {
    return store.state.user.selectedEstateId;
  });

  const expirationDate = ref<number | null>(null);
  const crumbs = computed(() => {
    return [
      {
        label: "Hold request",
        path: "/initial-hold",
      },
      {
        label: `Hold request ${
          purchaserDetails?.value
            ? `${purchaserDetails.value.firstName} - ${purchaserDetails.value.lastName}`
            : ""
        }`,
      },
    ];
  });

  const expirationDateText = computed(() => {
    if (!initialHold.value?.expirationDate) {
      return "-";
    }

    const formattedDate = formatDate(initialHold.value.expirationDate);

    if (initialHold.value.status === InitialHoldStatus.EXPIRED) {
      return `Expired on ${formattedDate}`;
    }

    if (
      initialHold.value.status === InitialHoldStatus.PENDING ||
      initialHold.value.status === InitialHoldStatus.APPROVED
    ) {
      return `Expires on ${formattedDate}`;
    }

    return "-";
  });

  const holdBusiness = computed(() => {
    return initialHold.value?.business;
  });

  const getUserBusiness = () => {
    loading.value = true;

    store
      .dispatch("business/getBusinessByLandownerId")
      .then((res: AxiosResponse<Business>) => {
        userBusiness.value = res.data;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const canManageLot = computed<boolean>(() => {
    return isLotOwner(lot.value as LotInterface, userBusiness.value);
  });

  const isSameBusinessOwner = computed<boolean>(() => {
    const initialHoldBusinessId = initialHold.value?.businessId;

    if (initialHoldBusinessId) {
      return initialHoldBusinessId === userBusiness.value?._id;
    }

    if (!lot.value?.ownershipId) {
      return true;
    }

    return canManageLot.value;
  });

  const onDeclineHold = (reasonForCancel: ReasonsForDecline | string) => {
    if (disabledDeclineButton.value) {
      return;
    }

    disabledDeclineButton.value = true;
    isDeclineModalOpen.value = false;
    loading.value = true;
    store
      .dispatch("initialHold/declineInitialHold", {
        id: initialHold.value?._id,
        reasonForCancel: reasonForCancel || null,
      })
      .then(() => {
        toast.success("Hold Request Declined");
        store.dispatch(
          "initialHold/getPendingInitialHoldsCounter",
          store.state.user.selectedEstateId
        );
        router.push({ name: "InitialHold" });
      })
      .finally(() => {
        loading.value = false;
        disabledDeclineButton.value = false;
      });
  };

  const getInitialHold = () => {
    loading.value = true;
    store
      .dispatch("initialHold/getInitialHoldById", {
        id: router.currentRoute.value.params.id,
        estateId: estateId.value,
      })
      .then(({ data }) => {
        const { initialHold: initialHoldData, lot: lotData } = data;

        initialHold.value = initialHoldData;
        lot.value = lotData;
        getPendingInitialHoldsCountByLot();
        getLotStage(lotData.stageId);
        updateButtonBasedOnHoldStatus(initialHoldData.status);
      })
      .finally(() => {
        loading.value = false;
      })
      .catch(() => {
        loading.value = false;
      });
  };
  const getPendingInitialHoldsCountByLot = () => {
    store
      .dispatch(
        "initialHold/getPendingInitialHoldsCountByLotId",
        lot.value?._id
      )
      .then((res) => {
        pendingInitialHoldsOnLotCount.value = res.data;
      });
  };
  const onExpiredHoldReason = (
    reasonForTermination: ReasonsForExpire | string
  ) => {
    if (
      initialHold.value?.status !== InitialHoldStatus.EXPIRED ||
      initialHold.value?.reasonForTermination
    ) {
      toast.error(
        "The hold request is not expired or the reason is already given"
      );
      return;
    }

    store
      .dispatch("initialHold/setReasonForTermination", {
        id: initialHold.value?._id,
        reasonForTermination,
      })
      .then(() => {
        toast.success("Thanks for giving a reason");
        isReasonForExpiredModalOpen.value = false;
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  };
  const getLotStage = async (stageId: string) => {
    store.dispatch("stage/getStage", stageId).then((res) => {
      stage.value = res.data;
    });
  };
  const onSetExpirationDateApproveModal = (date: number) => {
    expirationDate.value = date;
    isOpenApproveModal.value = false;
    isConfirmApproveModalOpen.value = true;
  };

  const updateButtonBasedOnHoldStatus = (status: InitialHoldStatus) => {
    const buttonStates = {
      [InitialHoldStatus.DECLINED]: { approve: true, decline: true },
      [InitialHoldStatus.EXPIRED]: { approve: true, decline: true },
      [InitialHoldStatus.CLOSED]: { approve: true, decline: true },
      [InitialHoldStatus.APPROVED]: {
        approve: true,
        decline: false,
      },
      [InitialHoldStatus.PENDING]: { approve: false, decline: false },
      [InitialHoldStatus.CANCELLED]: { approve: true, decline: true },
      default: { approve: true, decline: true },
    };
    const { approve, decline } = buttonStates[status] || buttonStates.default;

    disabledApproveButton.value = approve;
    disabledDeclineButton.value = decline;
  };

  const onCancelHold = (reasonForCancel: ReasonsForCancel | string) => {
    if (disableCancelButton.value) {
      return;
    }

    disableCancelButton.value = true;
    loading.value = true;
    isReasonForCancelModalOpen.value = false;
    store
      .dispatch("initialHold/cancelInitialHold", {
        id: initialHold.value?._id,
        reasonForCancel: reasonForCancel || null,
      })
      .then(() => {
        toast.success("Hold Request Cancelled");
        store.dispatch(
          "initialHold/getPendingInitialHoldsCounter",
          store.state.user.selectedEstateId
        );
        router.push({ name: "InitialHold" });
      })
      .catch(() => {
        toast.error("Something went wrong, please try again later");
      })
      .finally(() => {
        disableCancelButton.value = false;
        loading.value = false;
      });
  };
  const approveHoldRequest = () => {
    loading.value = true;
    isConfirmApproveModalOpen.value = false;
    store
      .dispatch("initialHold/approveInitialHold", {
        initialHoldId: initialHold.value?._id,
        expirationDate: expirationDate.value,
      })
      .then(() => {
        toast.success("Hold Request Approved");
        store.dispatch(
          "initialHold/getPendingInitialHoldsCounter",
          store.state.user.selectedEstateId
        );
        router.push({ name: "InitialHold" });
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const onSubmitExtendModal = (daysToExtend: number) => {
    loading.value = true;
    isExtendModalOpen.value = false;
    store
      .dispatch("initialHold/extendInitialHoldExpirationDate", {
        id: initialHold.value?._id,
        daysToExtend,
      })
      .then(() => {
        toast.success(
          `The hold request has been extended by ${daysToExtend} days`
        );
        store.dispatch(
          "initialHold/getPendingInitialHoldsCounter",
          store.state.user.selectedEstateId
        );
        router.push({ name: "InitialHold" });
      })
      .finally(() => {
        loading.value = false;
      });
  };

  onMounted(() => {
    if (isInitialHoldDisabled()) {
      router.push("/dashboard");
      return;
    }

    getInitialHold();
    getUserBusiness();
  });
  watch(
    () => initialHold.value?.status,
    () => {
      const querys = router.currentRoute.value.query;

      if (initialHold.value?.status === InitialHoldStatus.APPROVED) {
        isExtendModalOpen.value = querys.extendModal === "true";
      }

      if (querys.giveReason && querys.type === "expired") {
        isReasonForExpiredModalOpen.value = true;
      }
    },
    { immediate: true, deep: true }
  );

const __returned__ = { loading, toast, stage, disableCancelButton, initialHold, purchaserDetails, lot, userBusiness, isOpenApproveModal, disabledApproveButton, disabledDeclineButton, isConfirmCancelModalOpen, isConfirmApproveModalOpen, isDeclineModalOpen, isExtendModalOpen, isModalSubmitting, isReasonForCancelModalOpen, isReasonForExpiredModalOpen, pendingInitialHoldsOnLotCount, estateId, expirationDate, crumbs, expirationDateText, holdBusiness, getUserBusiness, canManageLot, isSameBusinessOwner, onDeclineHold, getInitialHold, getPendingInitialHoldsCountByLot, onExpiredHoldReason, getLotStage, onSetExpirationDateApproveModal, updateButtonBasedOnHoldStatus, onCancelHold, approveHoldRequest, onSubmitExtendModal, ref, onMounted, computed, watch, SetExpirationDateHoldRequestModal, get useToast() { return useToast }, get router() { return router }, get LotInterface() { return LotInterface }, get store() { return store }, ConfirmInitialHoldModal, ConfirmDeleteModal, get InitialHoldLeadsDetails() { return InitialHoldLeadsDetails }, get InitialHoldInterface() { return InitialHoldInterface }, get formatDate() { return formatDate }, Breadcrumbs, get InitialHoldStatus() { return InitialHoldStatus }, get LotStatus() { return LotStatus }, get isInitialHoldDisabled() { return isInitialHoldDisabled }, get getInitialHoldStatusText() { return getInitialHoldStatusText }, GiveReasonModal, InitialHoldLotDetailsCard, InitialHoldDetailsCard, get capitalizeString() { return capitalizeString }, get ReasonsForDecline() { return ReasonsForDecline }, get ReasonsForCancel() { return ReasonsForCancel }, get ReasonsForExpire() { return ReasonsForExpire }, get getInitialHoldStatusClass() { return getInitialHoldStatusClass }, get isLotOwner() { return isLotOwner }, get Business() { return Business }, get getUserData() { return getUserData }, get AxiosResponse() { return AxiosResponse } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})